import {
  DocumentTextIcon,
  ClockIcon,
  ChevronRightIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import ConfirmableButton from '../../../components/ConfirmableButton';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { BulkOperation } from '../apis/types';
import { useDeleteBulkOperation } from '../hooks/useDeleteBulkOperation';
import { getFormattedTime } from '../utils/timeUtils';

interface ImportCardProps {
  bulkOperation: BulkOperation;
}

const BulkUploadCard: React.FC<ImportCardProps> = ({ bulkOperation }) => {
  const { deleteBulkOperation, isDeleting } = useDeleteBulkOperation();

  // Determine header title
  const headerTitle = bulkOperation.title?.trim()
    ? bulkOperation.title
    : `Untitled import - ${getFormattedTime(bulkOperation.createdAt)}`;

  return (
    <div className='group relative bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 overflow-visible transition-colors'>
      {/* Delete Button - Top Right Corner */}
      <div className='absolute -top-3 -right-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity'>
        {isDeleting ? (
          <LoadingSpinner size='sm' />
        ) : (
          <ConfirmableButton
            onConfirm={() => deleteBulkOperation(bulkOperation.id)}
            buttonClassName='p-1 bg-white dark:bg-gray-800 rounded-full border border-blue-300 text-blue-500 hover:text-red-500 transition-colors'
            tooltip='Delete'
          >
            <TrashIcon className='w-6 h-6' />
          </ConfirmableButton>
        )}
      </div>

      {/* Header - Clickable */}
      <a
        tabIndex={0}
        href={`/bulk-upload/${bulkOperation.id}`}
        className='flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 cursor-pointer rounded-t-lg'
      >
        <div className='flex items-center space-x-3'>
          <DocumentTextIcon className='w-6 h-6 text-blue-600 dark:text-blue-400' />
          <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>
            {headerTitle}
          </h3>
        </div>
        <div className='flex items-center text-sm text-gray-500 dark:text-gray-400'>
          <ClockIcon className='w-4 h-4 mr-1' />
          {getFormattedTime(bulkOperation.createdAt)}
          <ChevronRightIcon className='w-5 h-5 ml-2 text-gray-400 dark:text-gray-500' />
        </div>
      </a>

      {/* Body */}
      <div className='px-6 py-4'>
        {/* Description */}
        {bulkOperation.description?.trim() && (
          <p className='text-sm text-gray-600 dark:text-gray-400 mb-4'>
            {bulkOperation.description}
          </p>
        )}

        {/* Receipt count (BulkOperation only has receiptIds, not inline receipt data) */}
        <div className='flex items-center space-x-8'>
          <div>
            <p className='text-3xl font-bold text-gray-900 dark:text-gray-100'>
              {bulkOperation.receiptIds.length}
            </p>
            <p className='text-sm text-gray-500 dark:text-gray-400 mt-1'>
              {bulkOperation.receiptIds.length === 1 ? 'Receipt' : 'Receipts'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadCard;
